import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';

const MetodosPago = () => {
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMetodo, setEditingMetodo] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMetodosPago();
  }, []);

  const fetchMetodosPago = async () => {
    setLoading(true);
    try {
      const response = await api.get('/metodos-pago');
      setMetodosPago(response.data);
    } catch (error) {
      message.error('Error al cargar métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        requiere_referencia: values.requiere_referencia !== false,
      };
      if (editingMetodo) {
        await api.put(`/metodos-pago/${editingMetodo.id}`, data);
        message.success('Método de pago actualizado exitosamente');
      } else {
        await api.post('/metodos-pago', data);
        message.success('Método de pago creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingMetodo(null);
      fetchMetodosPago();
    } catch (error) {
      message.error('Error al guardar método de pago');
    }
  };

  const handleEdit = (record) => {
    setEditingMetodo(record);
    form.setFieldsValue({
      ...record,
      requiere_referencia: record.requiere_referencia === 1 || record.requiere_referencia === true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/metodos-pago/${id}`);
      message.success('Método de pago eliminado exitosamente');
      fetchMetodosPago();
    } catch (error) {
      message.error('Error al eliminar método de pago');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Requiere Referencia',
      dataIndex: 'requiere_referencia',
      key: 'requiere_referencia',
      render: (requiere) => (
        <Tag color={requiere ? 'orange' : 'green'}>
          {requiere ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          activo: 'green',
          inactivo: 'red',
        };
        return <Tag color={colors[estado]}>{estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este método de pago?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Métodos de Pago"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingMetodo(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nuevo Método de Pago
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={metodosPago}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingMetodo ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingMetodo(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="codigo"
            label="Código"
            rules={[{ required: true, message: 'Ingresa el código' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="requiere_referencia"
            label="Requiere Referencia"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="activo">
            <Select>
              <Select.Option value="activo">Activo</Select.Option>
              <Select.Option value="inactivo">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingMetodo ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingMetodo(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MetodosPago;

